import { useCallback, useEffect, useState } from "react";
import { Search } from "react-bootstrap-table2-toolkit";
// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Container,
  Card,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@material-ui/core";
// @material-ui/lab components
import { Skeleton } from "@material-ui/lab";
// @material-ui/icons components
import {
  Create,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Done as DoneIcon,
} from "@material-ui/icons";

//Karsten
import HeaderClientes from "components/_refactor/headers/clients.js";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import IconButtonLoader from "components/Buttons/IconButtonLoader";
import ToolkitTable from "components/Cards/Tables/ToolkitTable";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import componentStyles from "assets/theme/components/cards/sortable/card-light-table-sortable.js";
import Api from "lib/api/httpRequestHelper";
import MaskAndValid from "utils/maskAndValid";
import useLogs from "hooks/logs";
import { IsMobile } from "utils/is-mobile";

const EditableCell = ({ initialLoading, row, changeIdLoja }) => {
  const [selectedStore, setSelectedStore] = useState();
  const [loading, setLoading] = useState(initialLoading);
  const [stores, setStores] = useState();

  const verifyCnpj =
    row?.CnpjPessoa?.length === 14
      ? row?.CnpjPessoa?.replace(
          /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
          "$1.$2.$3/$4-$5"
        )
      : row?.CnpjPessoa?.replace(
          /(\d{3})(\d{3})(\d{3})(\d{2})/g,
          "$1.$2.$3-$4"
        );

  const cancelPickUpChange = useCallback(() => {
    setStores();
    setSelectedStore();
  }, []);

  const handlePickUpEdit = useCallback(async () => {
    setLoading(true);

    let storeOptions = stores;

    if (!storeOptions) {
      await Api.get(
        "store",
        "commercialstructure",
        "?idMarca=" + row.IdMarca,
        true
      ).then(({ Result }) => {
        storeOptions = Result;
      });
    }
    setSelectedStore(
      storeOptions.find((pickUpStore) => pickUpStore.Id === row.IdPessoa)
    );

    setStores(storeOptions);

    setLoading(false);
  }, [stores, row.IdMarca, row.IdPessoa]);

  return loading ? (
    <Skeleton variant="text" />
  ) : stores ? (
    <Box display="flex" alignItems="center">
      <>
        <AutoCompleteField
          onChange={(newValue) => {
            setSelectedStore(newValue);
          }}
          value={selectedStore}
          options={stores}
          customTooltipText={(option) =>
            typeof option === "object"
              ? `${option.Id} - ${option.Apelido} - ${verifyCnpj}`
              : ""
          }
        />
        <Tooltip
          title="Cancelar alteração"
          style={{ marginLeft: 4, marginRight: 4 }}
        >
          <IconButtonLoader
            aria-label="botao para cancelar alteracao de loja"
            size="small"
            extraStyle={{
              width: 12,
              height: 12,
            }}
            onClick={() => cancelPickUpChange(row.IdPontoRetirada)}
            icon={<CancelIcon />}
          />
        </Tooltip>
        <Tooltip title="Confirmar alteração">
          <IconButtonLoader
            aria-label="botao para confirmar alteracao de loja"
            size="small"
            extraStyle={{
              width: 16,
              height: 16,
            }}
            onClick={() => changeIdLoja(row.IdPontoRetirada, selectedStore.Id)}
            icon={<DoneIcon />}
            disabled={!selectedStore || !selectedStore.Id}
          />
        </Tooltip>
      </>
    </Box>
  ) : row.IdPessoa && row.ApelidoPessoa ? (
    <Box display="flex" alignItems="center">
      <Tooltip title={`${row.Nome} - ${verifyCnpj}`} style={{ marginLeft: 2 }}>
        <div>{`${row.IdPessoa} - ${row.ApelidoPessoa}`}</div>
      </Tooltip>
      <Tooltip title="Alterar Loja" style={{ marginLeft: 2 }}>
        <IconButton
          aria-label="botao para alterar loja"
          size="small"
          onClick={() => {
            handlePickUpEdit(row);
          }}
        >
          <Box component={Create} width="10!important" height="10!important" />
        </IconButton>
      </Tooltip>
    </Box>
  ) : (
    <Tooltip title="Adicionar Loja" style={{ marginLeft: 2 }}>
      <Button
        aria-label="botao para adicionar loja"
        size="small"
        variant="text"
        onClick={() => {
          handlePickUpEdit(row);
        }}
        endIcon={<AddIcon />}
        style={{
          fontFamily: "Open Sans,sans-serif",
          fontWeight: 400,
          lineHeight: 1,
          fontSize: 13,
          padding: 0,
          display: "flex",
          alignItems: "center",
        }}
      >
        Adicionar loja
      </Button>
    </Tooltip>
  );
};

const getTableColumns = (changeIdLoja, searching) => [
  {
    dataField: "IdPontoRetirada",
    text: "IdPontoRetirada",
    sort: true,
    formatter: (id) => (searching ? <Skeleton variant="text" /> : id),
  },
  {
    dataField: "IdPessoa",
    text: "Loja",
    formatter: (cell, row) => {
      return (
        <EditableCell
          row={row}
          changeIdLoja={changeIdLoja}
          initialLoading={searching || row.isLoading}
        />
      );
    },
  },
  {
    dataField: "CnpjPessoa",
    text: "CNPJ Lojista",
    sort: true,
    formatter: (cnpj) =>
      searching ? <Skeleton variant="text" /> : MaskAndValid.maskCNPJ(cnpj),
  },
  {
    dataField: "Nome",
    text: "Nome",
    sort: true,
    formatter: (nome) =>
      searching ? <Skeleton variant="text" /> : nome || "-",
  },
  {
    dataField: "Logradouro",
    text: "Logradouro",
    sort: true,
    formatter: (logradouro) =>
      searching ? <Skeleton variant="text" /> : logradouro,
  },
  {
    dataField: "LogradouroNumero",
    text: "Número",
    sort: true,
    formatter: (logradouroNumero) =>
      searching ? <Skeleton variant="text" /> : logradouroNumero,
  },
  {
    dataField: "Bairro",
    text: "Bairro",
    sort: true,
    formatter: (bairro) => (searching ? <Skeleton variant="text" /> : bairro),
  },
  {
    dataField: "Cep",
    text: "Cep",
    sort: true,
    formatter: (cep) => (searching ? <Skeleton variant="text" /> : cep),
  },
  {
    dataField: "Cidade",
    text: "Cidade",
    sort: true,
    formatter: (cidade) => (searching ? <Skeleton variant="text" /> : cidade),
  },
  {
    dataField: "Uf",
    text: "UF",
    sort: true,
    formatter: (uf) => (searching ? <Skeleton variant="text" /> : uf),
  },
  {
    dataField: "PontoReferencia",
    text: "Ponto de Referência",
    sort: true,
    formatter: (referencia) =>
      searching ? <Skeleton variant="text" /> : referencia,
  },
  {
    dataField: "Ativo",
    text: "Ativo",
    sort: true,
    formatter: (ativo) =>
      searching ? <Skeleton variant="text" /> : MaskAndValid.formatYesNo(ativo),
  },
  {
    dataField: "DataSincronizacao",
    text: "Data de Sincronização",
    formatter: (data) =>
      searching ? (
        <Skeleton variant="text" />
      ) : (
        MaskAndValid.formattDateBR(data)
      ),
    sort: true,
  },
  {
    dataField: "Descricao",
    text: "Descrição",
    sort: true,
    formatter: (descricao) =>
      searching ? <Skeleton variant="text" /> : descricao,
  },
  {
    dataField: "InstrucoesRetirada",
    text: "Instruções de Retirada",
    sort: true,
    formatter: (instrucoes) =>
      searching ? <Skeleton variant="text" /> : instrucoes,
  },
  {
    dataField: "IdMarca",
    text: "IdMarca",
    formatter: (idMarca) => (searching ? <Skeleton variant="text" /> : idMarca),
    sort: true,
  },
];

const loadingItems = [
  { IdPontoRetirada: "0" },
  { IdPontoRetirada: "1" },
  { IdPontoRetirada: "2" },
  { IdPontoRetirada: "3" },
  { IdPontoRetirada: "4" },
  { IdPontoRetirada: "5" },
  { IdPontoRetirada: "6" },
  { IdPontoRetirada: "7" },
  { IdPontoRetirada: "8" },
  { IdPontoRetirada: "9" },
];

const { SearchBar } = Search;

const useStyles = makeStyles(componentStyles);

function PontoColetaVTEXLojista() {
  const classes = useStyles();
  const snackbar = useSnackbar();
  const { setLocalStorageActions, setLocalStorageApiProblem } = useLogs();

  const [pickUpPoints, setPickUpPoints] = useState(loadingItems);
  const [searching, setSearching] = useState(true);

  const isMobile = IsMobile();

  const updateList = useCallback(
    async (updatedPoint) => {
      const newPickUpList = pickUpPoints?.map((pickUpPoint) => {
        if (pickUpPoint.IdPontoRetirada === updatedPoint.IdPontoRetirada) {
          return { ...pickUpPoint, ...updatedPoint };
        }

        return pickUpPoint;
      });

      setPickUpPoints(newPickUpList);
    },
    [pickUpPoints]
  );

  const changeIdLoja = useCallback(
    async (pickUpPointId, newValue) => {
      if (newValue) {
        return await Api.postParams(
          "update-pick-up-point",
          "store",
          "?idPontoRetirada=" + pickUpPointId + "&idLoja=" + newValue,
          true
        )
          .then((res) => {
            if (res.Success) {
              snackbar.showMessage(`Ponto de Coleta atualizado com sucesso!`, {
                severity: "success",
              });
              updateList(res.Result);
            } else {
              snackbar.showMessage(
                `Ocorreu um erro ao atualizar o Ponto de Coleta. Verifique os parâmetros informados.`,
                {
                  severity: "error",
                }
              );
            }
          })
          .catch((error) => {
            snackbar.showMessage(error, {
              severity: "error",
            });
            setLocalStorageApiProblem({
              idMenuAccess: "104",
              screen: "Ponto de Coleta VTEX x Lojista",
              problem: `Erro na chamada do endpoint update-pick-up-point - ${error}`,
            });
          })
          .finally(() => setSearching(false));
      }

      return true;
    },
    [snackbar, updateList]
  );

  const fetchList = async () => {
    await Api.get("list-pick-up-point", "store", "", true)
      .then(({ Success, Result }) => {
        if (Success) {
          setPickUpPoints(Result);
        }
      })
      .catch((error) => {
        snackbar.showMessage(error, {
          severity: "error",
        });
        setLocalStorageApiProblem({
          idMenuAccess: "104",
          screen: "Ponto de Coleta VTEX x Lojista",
          problem: `Erro na chamada do endpoint list-pick-up-point - ${error}`,
        });
      })
      .finally(() => setSearching(false));
  };

  const handleRefresh = async () => {
    setSearching(true);

    return fetchList();
  };

  useEffect(() => {
    fetchList();
    setLocalStorageActions({
      nomeTela: "Ponto de Coleta VTEX x Lojista",
      idMenu: "104",
      acao: "Acessou o menu Ponto de Coleta VTEX x Lojista",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <HeaderClientes
        section="Ponto de Coleta VTEX x Lojista"
        subsection="Administração"
      />
      <Container style={{ marginTop: "-4.5rem" }} maxWidth={false}>
        <Grid container>
          <Grid item xs={12}>
            <Card classes={{ root: classes.cardRoot }}>
              <ToolkitTable
                keyField="IdPontoRetirada"
                dataTable={searching ? loadingItems : pickUpPoints}
                columnModel={getTableColumns(changeIdLoja, searching)}
                search
                allowEmpty
                CustomHeader={({ searchProps }) => (
                  <Grid
                    container
                    className="pl-4"
                    style={{ padding: isMobile ? "0 24px 10px" : "0 24px" }}
                  >
                    <Grid item xs={isMobile ? 12 : 6}>
                      <Box
                        component={Typography}
                        variant="h2"
                        marginBottom="0!important"
                      >
                        <Box component="span">Lista de Pontos de Coleta</Box>
                        <IconButtonLoader
                          size="medium"
                          extraStyle={{
                            width: 24,
                            height: 24,
                            marginLeft: "0.3rem",
                          }}
                          title="Recarregar lista"
                          onClick={handleRefresh}
                          icon={<RefreshIcon color="primary" />}
                          aria-label="botao para recarregar lista de pontos de coleta"
                          color="primary"
                          disabled={searching}
                        />
                      </Box>
                    </Grid>
                    <Grid
                      item
                      xs={isMobile ? 12 : 6}
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        justifyContent: isMobile ? "flex-start" : "flex-end",
                      }}
                    >
                      <SearchBar
                        className="form-control"
                        placeholder="Pesquisar Pontos de Coleta"
                        style={{ width: 250 }}
                        {...searchProps}
                        disabled={searching}
                      />
                    </Grid>
                  </Grid>
                )}
                pagination={{
                  options: [25, 50, 100],
                  defaultOption: 25,
                }}
              />
            </Card>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}

PontoColetaVTEXLojista.propTypes = {};

export default PontoColetaVTEXLojista;
